import {Plain} from '../../../../shared/model/plain.type.model';
import {DisplayOptions} from './conditions/display.options.model';
import {ElementPage} from './elements/element.page.model';
import {OPERATOR_LOGICAL} from './conditions/operator.logical.model';
import {QUESTION_TYPE} from './elements/question.type.model';
import {toElementPage} from './elements/to-element-page.model';

const QUESTION_TYPES: string[] = Object.values(QUESTION_TYPE);

export class FormPage {
    /** `null` tant que le serveur n'a pas attribué le sien, à l'enregistrement du brouillon. */
    public id: number | null;
    public stageId?: number;
    public title: Record<string, string>;
    public description: Record<string, string>;
    public elements: ElementPage[];
    public displayOptions: DisplayOptions;

    constructor(data: Plain<FormPage>) {
        this.id = data.id;
        this.stageId = data.stageId;
        // L'API omet les champs non renseignés : on normalise en structures vides, jamais en null.
        this.title = data.title ?? {};
        this.description = data.description ?? {};
        this.elements = (data.elements ?? []).map(toElementPage);
        this.displayOptions = new DisplayOptions(data.displayOptions);
    }

    /**
     * Les conditions qui décident de l'affichage de cette page sont-elles remplies ?
     *
     * Une page conditionnée par une condition à moitié composée ne s'afficherait jamais : le
     * questionnaire s'arrêterait devant elle. Une page toujours affichée n'a rien à remplir.
     */
    public readonly hasCompleteDisplayOptions = (): boolean => this.displayOptions.isComplete();

    /**
     * Quelque chose ici se montre-t-il sous conditions — la page elle-même, ou l'un de ses éléments ?
     *
     * C'est ce qui décide de la marque posée sur la vignette : une page dont rien n'est conditionné
     * n'a rien à signaler. À la différence de `hasCompleteDisplayOptions`, qui ne regarde que la
     * page, celle-ci embrasse tout ce que la vignette représente.
     */
    public readonly hasDisplayConditions = (): boolean =>
        this.displayOptions.groups.length > 0 ||
        this.elements.some((element) => element.displayOptions.groups.length > 0);

    /**
     * L'une de ces conditions est-elle restée à moitié composée ? La marque de la vignette passe
     * alors à l'orange : ce qu'elle règle ne s'afficherait jamais.
     */
    public readonly hasIncompleteDisplayConditions = (): boolean =>
        !this.displayOptions.isComplete() || this.incompleteConditionElements().length > 0;

    /**
     * Les éléments de la page dont les conditions sont restées à moitié composées. Le détail que
     * la vignette livre au survol de sa marque : savoir que quelque chose cloche sur la page ne dit
     * pas encore où, et une page en porte plusieurs.
     */
    public readonly incompleteConditionElements = (): ElementPage[] =>
        this.elements.filter((element) => !element.displayOptions.isComplete());

    /**
     * Les questions de la page qui se mesurent étape par étape sans en désigner aucune.
     *
     * Le détail que la vignette livre au survol de sa marque : savoir qu'une question manque son
     * étape ne dit pas encore laquelle, et une page en porte plusieurs.
     *
     * La page ne sait pas si l'enquête procède par étapes — c'est à l'appelant de ne poser la
     * question que là où elle a un sens. Ici, seul le type de la question compte.
     */
    public readonly unstagedQuestions = (): ElementPage[] =>
        this.elements.filter(
            (element) => element.isStaged() && (element.stageId === undefined || element.stageId === null)
        );

    /**
     * La page pose-t-elle au moins une question qui se mesure étape par étape, étape désignée ou non ?
     *
     * C'est ce qui rend possible l'affectation de toute la page à une même étape : sans une telle
     * question, il n'y aurait rien à rattacher.
     */
    public readonly hasStagedQuestion = (): boolean => this.elements.some((element) => element.isStaged());

    /** La page pose-t-elle un niveau d'effort ? Il ne se mesure qu'une fois par étape. */
    public readonly hasCes = (): boolean => this.elements.some((element) => element.isCes());

    /**
     * La page pose-t-elle au moins une question ?
     *
     * Une page qui n'en pose aucune ne demande rien au répondant : il la traverse sans avoir à
     * répondre, et elle ne rapporte rien. Elle peut naître de la suppression de sa dernière
     * question — l'éditeur la laisse faire, mais la diffusion s'en trouve retenue.
     *
     * Les éléments qui n'attendent pas de réponse — zone de texte, invitation à déposer un avis —
     * ne comptent pas : une page qui n'en porterait que ne poserait toujours aucune question.
     */
    public readonly hasQuestion = (): boolean => this.elements.some((element) => element.isQuestion());

    /**
     * Vignette qui représente la page : le dessin de sa première question. Une page vide, ou qui
     * commence par un élément sans question (zone de texte, lien vers un site d'avis), se présente
     * comme un NPS, faute de mieux.
     */
    public readonly thumbnailFileName = (): string => {
        const type = this.elements[0]?.type;
        const questionType = type && QUESTION_TYPES.includes(type) ? type : QUESTION_TYPE.NPS;

        return `${questionType.toLowerCase()}-question.svg`;
    };
}

/**
 * Page ajoutée au questionnaire : elle démarre avec l'élément qu'on lui donne, pour que
 * l'utilisateur ait quelque chose à modifier plutôt qu'une page blanche.
 *
 * Lequel, c'est à l'appelant de le dire : ce qu'est une question ouverte — ses contraintes, son
 * message d'obligation, ses options d'affichage — est décrit par le catalogue du serveur, pas ici.
 * Sans élément, la page arrive vide ; mieux vaut cela qu'une question inventée sur place, qui
 * repartirait au serveur amputée de son paramétrage.
 *
 * L'identifiant de la page reste `null` jusqu'à l'enregistrement, où le serveur attribue le sien.
 */
export const buildFormPage = (firstElement?: ElementPage): FormPage =>
    new FormPage({
        id: null,
        title: {default: ''},
        description: {default: ''},
        displayOptions: {operator: OPERATOR_LOGICAL.AND, groups: []},
        elements: firstElement ? [firstElement] : [],
    } as unknown as Plain<FormPage>);
